import { some } from '@utils/some';
import moment from 'moment';
import { AvailabilitySlot } from 'shared-types';
import { TimeSlotType } from 'shared-types/SpaPackage';
import { ExperienceType, SelectedExperienceType } from '../types';
import { getSelectedTimeslotsWithStaffAndRoomAllocation } from './getSelectedTimeslotsWithStaffAndRoomAllocation';
import { hasStaffAndRoomAvailability } from './hasStaffAndRoomAvailability';
import { isOverlapping } from './isTimeslotOverlapping';
import { mergeEquivalentTimeslots } from './timeslotCapacity';

export type ExperienceTimeslotOption = AvailabilitySlot & {
  label: string;
  value: Date;
  experienceId: number;
  type: ExperienceType;
  delayAfter: number;
  optionGroupId?: number | string;
  optionId: number | string;
  startTime: string;
  endTime: string;
  visitDate: string;
  quantity: number | null;
  staff?: string[];
  rooms?: string[];
  dayNumber: number;
  isUnavailable?: boolean;
};

type GuestExperiences = { bookings?: any[] }[];

export interface BuildExperienceTimeslotOptionsParams {
  timeslots: TimeSlotType[];
  experienceId?: number;
  entityType: ExperienceType;
  delayAfter?: number;
  optionId?: number | string;
  optionGroupId?: number | string;
  experienceDate: string;
  activeDayNumber?: number;
  selectedExperiences?: SelectedExperienceType[];
  allGuestExperiences: GuestExperiences;
  guestIndex: number;
  reservedSpaTimeSlots: string[];
  minimumTimeslotQuantity?: number;
  currentBookingExperienceId?: number;
  /**
   * Narrows "the booking being edited" to one option. Without it an experience
   * offered by two groups hides the guest's own clash between them.
   */
  currentBookingOptionId?: number | string;
  /**
   * Keeps slots the guest's own earlier booking clashes with, flagged
   * `isUnavailable`, so a caller can grey them out and say why. Capacity and
   * staff/room exclusions are always dropped — neither is actionable.
   */
  includeOwnClashes?: boolean;
}

const mergedTimeslotCache = new WeakMap<TimeSlotType[], TimeSlotType[]>();

/** Callers render a card at a time, so the same array is merged repeatedly. */
const mergeOnce = (timeslots: TimeSlotType[]): TimeSlotType[] => {
  if (!Array.isArray(timeslots) || !timeslots.length) return [];

  const cached = mergedTimeslotCache.get(timeslots);
  if (cached) return cached;

  const merged = mergeEquivalentTimeslots(timeslots);
  mergedTimeslotCache.set(timeslots, merged);
  return merged;
};

/** Lifted from AvailabilityForm so both ecoms offer the same slots. */
export const buildExperienceTimeslotOptions = ({
  timeslots,
  experienceId,
  entityType,
  delayAfter,
  optionId,
  optionGroupId,
  experienceDate,
  activeDayNumber,
  selectedExperiences,
  allGuestExperiences,
  guestIndex,
  reservedSpaTimeSlots,
  minimumTimeslotQuantity = 1,
  currentBookingExperienceId,
  currentBookingOptionId,
  includeOwnClashes = false,
}: BuildExperienceTimeslotOptionsParams): ExperienceTimeslotOption[] => {
  const isBookingBeingEdited = (slot: any) =>
    slot.experienceId === currentBookingExperienceId &&
    (currentBookingOptionId === undefined ||
      Number(slot.optionId) === Number(currentBookingOptionId));

  const guestExistingTimeslots = allGuestExperiences?.[guestIndex]?.bookings?.filter(
    (slot) => slot && !isBookingBeingEdited(slot) && slot.dayNumber === activeDayNumber
  );

  const experienceExistingTimeslots = allGuestExperiences
    ?.filter((_, index) => index !== guestIndex)
    .flatMap(
      (guest) =>
        guest.bookings?.filter(
          (ts) =>
            ts &&
            ts.startTime &&
            ts.endTime &&
            ts.experienceId === experienceId &&
            ts.dayNumber === activeDayNumber
        ) || []
    );

  const selectedTimeslotsWithStaffAndRoomAllocation =
    getSelectedTimeslotsWithStaffAndRoomAllocation(allGuestExperiences as any);

  const availableTimeOptions = mergeOnce(timeslots).filter(
    (time) => !reservedSpaTimeSlots.includes(time?.startTime.toString())
  );

  const arrivalTimesOptions = availableTimeOptions.reduce(
    (acc: ExperienceTimeslotOption[], timeslot) => {
      const experience = (selectedExperiences || []).find((exp) => {
        if (Number(exp.id) !== Number(experienceId)) return false;
        if (optionId !== undefined && Number(exp.optionId) !== Number(optionId)) return false;
        if (optionGroupId !== undefined && Number(exp.optionGroupId) !== Number(optionGroupId)) {
          return false;
        }
        return true;
      });

      if (experience && experienceId && experienceDate && activeDayNumber !== undefined) {
        acc.push({
          ...timeslot,
          label: timeslot.startTime,
          delayAfter: delayAfter || 0,
          value: new Date(`${experienceDate} ${timeslot.startTime}`),
          experienceId,
          type: entityType,
          optionId: optionId ?? experience.optionId,
          optionGroupId: optionGroupId ?? experience.optionGroupId,
          startDateTime: new Date(timeslot.startTime),
          endDateTime: new Date(timeslot.endTime),
          bandNumber: 0,
          discount: undefined,
          dayNumber: activeDayNumber,
          visitDate: experienceDate,
          staff: timeslot.staff,
          rooms: timeslot.rooms,
        } as ExperienceTimeslotOption);
      }
      return acc;
    },
    []
  );

  const hasSameGuestForTimeslot = (timeslot: ExperienceTimeslotOption) =>
    !some(
      guestExistingTimeslots,
      (existingTimeslot) =>
        existingTimeslot &&
        timeslot.type !== 'spa-facilities' &&
        existingTimeslot.type !== 'spa-facilities' &&
        isOverlapping(timeslot as any, existingTimeslot)
    );

  const hasQuantityAndTimeslotAvailable = (timeslot: ExperienceTimeslotOption) => {
    if (timeslot.quantity !== null && timeslot.quantity < minimumTimeslotQuantity) {
      return false;
    }

    const [timeslotStartHour, timeslotStartMinute] = timeslot.startTime.split(':');

    return !some(experienceExistingTimeslots, (existingTimeslot) => {
      const [existingTimeslotStartHour, existingTimeslotStartMinute] = (
        existingTimeslot?.startTime || ''
      ).split(':');

      const isStillAvailableAfterUserSelection =
        timeslot.quantity && timeslot?.quantity <= experienceExistingTimeslots.length;

      const isAtTheSameTimeAsUserSelection = moment(experienceDate)
        .set({
          hour: Number(existingTimeslotStartHour),
          minute: Number(existingTimeslotStartMinute),
        })
        .isSame(
          moment(experienceDate).set({
            hour: Number(timeslotStartHour),
            minute: Number(timeslotStartMinute),
          })
        );
      return isStillAvailableAfterUserSelection && isAtTheSameTimeAsUserSelection;
    });
  };

  const hasAvailableResources = (timeslot: ExperienceTimeslotOption) => {
    if (timeslot.staff === undefined && timeslot.rooms === undefined) {
      return true;
    }
    return hasStaffAndRoomAvailability(
      timeslot as any,
      selectedTimeslotsWithStaffAndRoomAllocation,
      guestIndex
    );
  };

  return arrivalTimesOptions
    .filter(
      (timeslot) => hasQuantityAndTimeslotAvailable(timeslot) && hasAvailableResources(timeslot)
    )
    .reduce<ExperienceTimeslotOption[]>((acc, timeslot) => {
      if (hasSameGuestForTimeslot(timeslot)) {
        acc.push(timeslot);
      } else if (includeOwnClashes) {
        acc.push({ ...timeslot, isUnavailable: true });
      }
      return acc;
    }, []);
};
